import {
  APPOINTMENT_DEFAULT_DURATION,
  freeTimeFrom,
  slotTaken,
  type AssignedInquiry,
  type PlannedInternalMeeting,
} from '@ghp/domain'

/**
 * Ce qui occupe déjà l'équipe, tel que la tablette l'a descendu : la tournée et ses points
 * internes (#669). Les écrans le lisent en cache local (`useTourOccupancy`), la pré-fiche le
 * compare au créneau que l'agent pose.
 */
export interface TourOccupancy {
  inquiries: readonly AssignedInquiry[]
  meetings: readonly PlannedInternalMeeting[]
}

/** Ce que le créneau recouvre : un rendez-vous de la tournée, ou un point interne. */
export type TakenTourSlot = { scheduledAt: string; durationMinutes: number } & (
  { inquiry: AssignedInquiry } | { meeting: PlannedInternalMeeting }
)

/**
 * La base ouverte est celle de l'équipe de la session (`useTeamData`) : tout ce qu'elle porte
 * l'occupe. Une seule équipe, donc — celle de la tournée, que `slotTaken` compare à elle-même.
 */
const TOUR_TEAM = { id: 'tour' }

/**
 * Le premier rendez-vous ou point interne de la tournée que ce créneau recouvre, durée comprise
 * (`slotTaken`), sans la demande qu'on planifie. `null` quand le créneau est libre.
 */
export function tourSlotTaken(
  tour: TourOccupancy,
  scheduledAt: string,
  durationMinutes: number,
  except: string,
): TakenTourSlot | null {
  return slotTaken(occupiedBy(tour), TOUR_TEAM.id, scheduledAt, durationMinutes, except)
}

/**
 * L'heure où le créneau tient sans toucher la tournée (`freeTimeFrom`, #692) : celle que
 * « Replanifier » propose. `null` quand plus rien ne se libère ce jour-là.
 */
export function tourFreeTimeFrom(
  tour: TourOccupancy,
  scheduledAt: string,
  durationMinutes: number,
  except: string,
): string | null {
  return freeTimeFrom(occupiedBy(tour), [TOUR_TEAM.id], scheduledAt, durationMinutes, except)
}

/**
 * Ce qui occupe la tournée, comme la semaine le sert au bureau. Un rendez-vous sans durée — une
 * ligne d'avant #431 — compte une heure, comme sa carte.
 */
function occupiedBy(tour: TourOccupancy): TakenTourSlot[] {
  const appointments = tour.inquiries.flatMap((inquiry) => {
    // `?? null` : une ligne en cache d'avant le champ le relit `undefined` (D22).
    const at = inquiry.scheduledAt ?? null

    return at === null
      ? []
      : [
          {
            scheduledAt: at,
            durationMinutes: inquiry.appointmentDurationMinutes ?? APPOINTMENT_DEFAULT_DURATION,
            team: TOUR_TEAM,
            inquiry,
          },
        ]
  })
  // `teams`, comme le sert la semaine : `slotTaken` y reconnaît un point interne, et compte les
  // journées entières d'un séminaire (#670).
  const meetings = tour.meetings.map((meeting) => ({
    id: meeting.id,
    scheduledAt: meeting.scheduledAt,
    durationMinutes: meeting.durationMinutes,
    teams: [TOUR_TEAM],
    meeting,
  }))

  return [...appointments, ...meetings]
}
